import type * as hast from 'hast';
import parseStyle from 'style-to-object';

import {
    icon,
    MARK_BOLD,
    MARK_CODE,
    MARK_COLOR,
    MARK_COLOR_VALUES,
    MARK_ITALIC,
    MARK_KEYBOARD,
    MARK_STRIKETHROUGH,
    MARK_SUBSCRIPT,
    MARK_SUPERSCRIPT,
} from '@gitbook/doc-core';
import { Mark, Text } from '@gitbook/slate';

import { processInlineExpressionElement } from './transform-expressions';
import { processMathElement } from './transform-images-maths';
import type { HastToGastTransformFn, HastToGastTransformMap } from './types';
import { getClassNamesAttribute } from './utils';

const markCodeTransform = createMarkTransform(MARK_CODE);

/**
 * Map of tag names to their corresponding transforms.
 */
export const transformMarks: HastToGastTransformMap = {
    b: createMarkTransform(MARK_BOLD),
    strong: createMarkTransform(MARK_BOLD),
    i: (node, ...rest) => {
        const classNames = getClassNamesAttribute(node.properties);
        const faClass = classNames.find((c) => c.startsWith('fa-'));
        if (faClass) {
            const iconName = faClass.slice('fa-'.length);
            return [icon.createIcon(iconName)];
        }

        return createMarkTransform(MARK_ITALIC)(node, ...rest);
    },
    s: createMarkTransform(MARK_STRIKETHROUGH),
    del: createMarkTransform(MARK_STRIKETHROUGH),
    sup: createMarkTransform(MARK_SUPERSCRIPT),
    sub: createMarkTransform(MARK_SUBSCRIPT),
    em: createMarkTransform(MARK_ITALIC),
    kbd: createMarkTransform(MARK_KEYBOARD),
    cite: createMarkTransform(MARK_ITALIC),
    code: (node, ...rest) => {
        // handle <code><pre /></code> which is really a `pre`
        if (node.children.some((child) => child.type === 'element' && child.tagName === 'pre')) {
            return;
        }

        // handle <code> with class expression
        const expression = processInlineExpressionElement(node);
        if (expression) {
            return expression;
        }

        // handle <code> with language-math/math-inline
        const math = processMathElement(node);
        if (math) {
            return math;
        }

        return markCodeTransform(node, ...rest);
    },
    br: () => {
        return [Text.create('\n')];
    },
    mark: (node, eat, context) => {
        const { style } = node.properties || {};
        if (typeof style !== 'string') {
            return;
        }

        const styleObject = parseStyle(style) || {};
        const textColor = styleObject.color;
        const backgroundColor = styleObject['background-color'];
        if (!textColor && !backgroundColor) {
            return;
        }

        // Ensure that we keep marks in alphabetical order, which helps for consistency
        // in tests and when doing bidirectional import/export.
        const marks = [
            ...(context.marks || []),
            Mark.create({
                type: MARK_COLOR,
                data: {
                    ...(textColor && MARK_COLOR_VALUES.includes(textColor)
                        ? { text: textColor }
                        : {}),
                    ...(backgroundColor && MARK_COLOR_VALUES.includes(backgroundColor)
                        ? { background: backgroundColor }
                        : {}),
                },
            }),
        ];
        marks.sort((lhs, rhs) => rhs.type.localeCompare(lhs.type));

        return eat('inlines', node.children, {
            ...context,
            marks,
        });
    },
};

/**
 * Create transform for a mark
 */
function createMarkTransform(markType: string): HastToGastTransformFn {
    return (node: hast.Element, eat, context) => {
        // Ensure that we keep marks in alphabetical order, which helps for consistency
        // in tests and when doing bidirectional import/export.
        const marks = [...(context.marks || []), Mark.create(markType)];
        marks.sort((lhs, rhs) => rhs.type.localeCompare(lhs.type));

        return eat('inlines', node.children, {
            ...context,
            marks,
        });
    };
}
